import { useState, useEffect } from 'react';
import {
  IconLink,
  IconStarFilled,
  IconGlassFilled,
  IconChefHatFilled,
  IconTrashXFilled,
  IconChevronDownFilled,
  IconPlusFilled,
  IconSearchFilled,
  IconPhotoFilled,
  IconBoxMultipleFilled,
} from '@tabler/icons-react';
import TipBlue from '../Utils/TipBlue';
import { ensureMinLoading } from '../Utils/withLoading';
import { toDirectDriveUrl } from '../Utils/driveImage';
import DeleteIconCircle from './DeleteIconCircle';
import EmptyState from './EmptyState';
import { apiFetch } from '../api/client';

const FONT = '"Public Sans", sans-serif';
const AMBER = '#f87c2b';
const GREEN = '#30d158';

const inputClass =
  'w-full outline-none bg-white/[0.08] focus:bg-white/[0.12] focus:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)] placeholder:text-white/30 transition-all duration-200 ease-out';

const inputStyle = {
  border: 'none',
  borderRadius: '16px',
  height: '48px',
  padding: '0 16px',
  fontSize: '15px',
  fontWeight: 500,
  fontFamily: FONT,
  color: 'rgba(255, 255, 255, 0.95)',
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle = {
  fontSize: '13px',
  lineHeight: '18px',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.45)',
  fontFamily: FONT,
};

function SelectorField({ label, placeholder, hasValue, value, open, onToggle, children }) {
  return (
    <div className="flex flex-col gap-2">
      <label style={labelStyle}>{label}</label>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex items-center justify-between gap-3 w-full h-12 px-4 rounded-2xl border-none cursor-pointer text-left transition-colors duration-200 ease-out [@media(hover:hover)]:hover:bg-white/[0.1]"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)' }}
      >
        <span
          className="truncate"
          style={{
            fontSize: '15px',
            fontWeight: 500,
            fontFamily: FONT,
            color: hasValue ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.4)',
          }}
        >
          {hasValue ? value : placeholder}
        </span>
        <span
          className="flex items-center justify-center shrink-0 w-7 h-7 rounded-full"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.12)' }}
        >
          <IconChevronDownFilled
            size={13}
            color="rgba(255, 255, 255, 0.7)"
            style={{
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          />
        </span>
      </button>
      {open && (
        <div
          className="flex flex-wrap gap-1.5 rounded-2xl p-3"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function Chip({ selected, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`flex items-center justify-center h-10 px-4 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out ${
        selected ? '' : '[@media(hover:hover)]:hover:bg-white/[0.1]'
      }`}
      style={{
        backgroundColor: selected ? '#fff' : 'rgba(255, 255, 255, 0.06)',
        fontSize: '13px',
        fontWeight: 600,
        fontFamily: FONT,
        color: selected ? '#000' : 'rgba(255, 255, 255, 0.75)',
      }}
    >
      {children}
    </button>
  );
}

export default function ModalDetallesProducto({ isOpen, onClose, onUpdated, product, dynamicIslandRef }) {
  const [form, setForm] = useState({
    nombre: '',
    precio: '',
    category_id: null,
    category_ids: [],
    variantes: [],
    descripcion: '',
    recetaActiva: false,
    ingredientes: [],
    destacado: false,
    vista: 'barra',
    imagenes: [],
  });
  const [categories, setCategories] = useState([]);
  const [variantGroups, setVariantGroups] = useState([]);
  const [assignedGroupIds, setAssignedGroupIds] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventorySearch, setInventorySearch] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showCategoriasDropdown, setShowCategoriasDropdown] = useState(false);
  const [showVariantesDropdown, setShowVariantesDropdown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [driveUrl, setDriveUrl] = useState('');
  const [activeTab, setActiveTab] = useState('datos');
  const [commissionRate, setCommissionRate] = useState(3.49);
  const [taxRate, setTaxRate] = useState(16);

  useEffect(() => {
    if (isOpen && product) {
      setAssignedGroupIds([]);
      setInventorySearch('');
      setSelectedImageIndex(0);
      setSaving(false);
      setDriveUrl('');
      fetchCategories();
      fetchVariantGroups();
      fetchInventory();
      apiFetch(`/settings`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data && data.mp_commission_credit_rate != null) {
            setCommissionRate(Number(data.mp_commission_credit_rate));
          } else if (data && data.mp_commission_rate != null) {
            setCommissionRate(Number(data.mp_commission_rate));
          }
          if (data && data.tax_rate != null) {
            setTaxRate(Number(data.tax_rate));
          }
        })
        .catch(() => {});
      if (product.id) {
        fetchProductData(product.id);
      }
    }
  }, [isOpen, product]);

  const fetchProductData = async (productId) => {
    try {
      const res = await apiFetch(`/products/${productId}`);
      if (!res.ok) return;
      const data = await res.json();
      const cats = data.categories || (data.category_id ? [{ id: data.category_id }] : []);

      const recipeRes = await apiFetch(`/products/${productId}/recipes`);
      const recipes = recipeRes.ok ? await recipeRes.json() : [];

      const varRes = await apiFetch(`/variants/by-item/${productId}`);
      const vars = varRes.ok ? await varRes.json() : [];
      const variantIds = vars.map((g) => g.id);
      setAssignedGroupIds(variantIds);

      setForm({
        nombre: data.name || '',
        precio: String(data.price ?? ''),
        category_id: cats.length > 0 ? cats[0].id : data.category_id || null,
        category_ids: cats.map((c) => c.id),
        variantes: variantIds,
        descripcion: data.description || '',
        recetaActiva: recipes.length > 0,
        ingredientes: recipes.map((r) => ({
          inventory_id: r.inventory_id,
          inventory_name: r.inventory_name,
          quantity: String(r.quantity),
          unit_of_measure: r.unit_of_measure,
        })),
        destacado: data.is_featured ?? false,
        vista: data.view || 'barra',
        imagenes: data.img_url ? [data.img_url] : [],
      });
    } catch {}
  };

  const fetchCategories = async () => {
    try {
      const res = await apiFetch(`/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch {}
  };

  const fetchInventory = async () => {
    try {
      const res = await apiFetch(`/inventory`);
      if (res.ok) {
        const data = await res.json();
        setInventoryItems(data);
      }
    } catch {}
  };

  const fetchVariantGroups = async () => {
    try {
      const res = await apiFetch(`/variants/groups`);
      if (res.ok) {
        const data = await res.json();
        setVariantGroups(data);
      }
    } catch {}
  };



  if (!isOpen || !product) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleVariante = (id) => {
    setForm((prev) => {
      const current = prev.variantes;
      const next = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      return { ...prev, variantes: next };
    });
  };

  // Las imágenes se pegan como URL compartida de Google Drive y se guardan
  // ya normalizadas al CDN de contenido (lh3.googleusercontent.com/d/ID=s0).
  const handleAddDriveImage = () => {
    const direct = toDirectDriveUrl(driveUrl);
    if (!direct) {
      dynamicIslandRef?.current?.addAlert('error', 'Pega una URL válida de Google Drive.');
      return;
    }
    setForm((prev) => ({ ...prev, imagenes: [...prev.imagenes, direct] }));
    setSelectedImageIndex(form.imagenes.length);
    setDriveUrl('');
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) return;
    if (!form.precio || isNaN(parseFloat(form.precio))) return;
    if (form.category_ids.length === 0) return;

    setSaving(true);
    dynamicIslandRef?.current?.addAlert('loading', 'Actualizando producto...');
    const startedAt = Date.now();

    try {
      const body = {
        name: form.nombre.trim(),
        price: parseFloat(form.precio),
        category_id: form.category_id,
        category_ids: form.category_ids,
      };
      if (form.descripcion.trim()) body.description = form.descripcion.trim();
      if (form.imagenes.length > 0) body.img_url = form.imagenes[0];
      body.is_featured = form.destacado;
      body.view = form.vista;

      const res = await apiFetch(`/products/${product.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let msg = 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.';
        try {
          const err = await res.json();
          if (err.detail) msg = err.detail;
          else if (Array.isArray(err.detail)) msg = err.detail.map(d => d.msg).join(', ');
        } catch {}
        dynamicIslandRef?.current?.clearAlerts();
        dynamicIslandRef?.current?.addAlert('error', msg);
        return;
      }

      {
        const recipesBody = form.ingredientes.length > 0
          ? { recipes: form.ingredientes.map((ing) => ({
              inventory_id: ing.inventory_id,
              quantity: parseFloat(ing.quantity) || 1,
            }))}
          : { recipes: [] };

        const recipeRes = await apiFetch(`/products/${product.id}/recipes`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(recipesBody),
        });
        if (!recipeRes.ok) {
          dynamicIslandRef?.current?.clearAlerts();
          dynamicIslandRef?.current?.addAlert('error', 'Producto actualizado, pero ocurrió un error al guardar la receta.');
          return;
        }
      }

      const removedIds = assignedGroupIds.filter((id) => !form.variantes.includes(id));
      const addedIds = form.variantes.filter((id) => !assignedGroupIds.includes(id));
      if (removedIds.length > 0 || addedIds.length > 0) {
        dynamicIslandRef?.current?.addAlert('loading', 'Guardando variantes...');
      }
      for (const groupId of removedIds) {
        await apiFetch(`/variants/assignments`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ item_id: product.id, group_id: groupId }),
        });
      }
      for (const groupId of addedIds) {
        await apiFetch(`/variants/assignments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ item_id: product.id, group_id: groupId }),
        });
      }

      await fetchProductData(product.id);
      await ensureMinLoading(startedAt);
      dynamicIslandRef?.current?.clearAlerts();
      dynamicIslandRef?.current?.addAlert('success', 'Producto actualizado correctamente');
      onUpdated?.();
    } catch (e) {
      dynamicIslandRef?.current?.clearAlerts();
      dynamicIslandRef?.current?.addAlert('error', 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.');
    } finally {
      setSaving(false);
    }
  };

  const canSave = form.nombre.trim() && form.precio && !isNaN(parseFloat(form.precio)) && form.category_ids.length > 0 && !saving;

  const filteredInventory = inventoryItems.filter(
    (inv) =>
      (inv.name.toLowerCase().includes(inventorySearch.toLowerCase()) ||
        (inv.sku && inv.sku.toLowerCase().includes(inventorySearch.toLowerCase())))
  );

  const selectedCategoryNames = form.category_ids
    .map((id) => categories.find((c) => c.id === id)?.name)
    .filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-3"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Editar producto"
        className="flex flex-col overflow-hidden w-full"
        style={{
          backgroundColor: '#000',
          borderRadius: '28px',
          border: '1px solid rgba(255, 255, 255, 0.16)',
          maxWidth: '980px',
          height: 'min(820px, calc(100dvh - 24px))',
          boxShadow: '0 40px 120px rgba(0, 0, 0, 0.8)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-5 sm:px-6 pt-5 pb-4 border-b border-white/[0.16]">
          <div className="min-w-0">
            <h2
              className="font-semibold m-0 truncate"
              style={{
                fontSize: '22px',
                lineHeight: '28px',
                letterSpacing: '-0.02em',
                color: 'rgba(255, 255, 255, 0.95)',
                fontFamily: FONT,
              }}
            >
              Editar producto
            </h2>
            <p
              className="m-0 mt-1 truncate"
              style={{
                fontSize: '13px',
                lineHeight: '18px',
                fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.45)',
                fontFamily: FONT,
              }}
            >
              {form.nombre || 'Datos, imagen y receta'} · #{product.id}
            </p>
          </div>

          <div
            className="inline-flex gap-1 mt-4 p-1 rounded-full"
            role="tablist"
            aria-label="Secciones del producto"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.035)' }}
          >
            {[
              { id: 'datos', label: 'Datos' },
              { id: 'ingredientes', label: 'Ingredientes' },
            ].map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center justify-center h-10 px-5 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out ${
                    isActive ? '' : '[@media(hover:hover)]:hover:bg-white/[0.06]'
                  }`}
                  style={{
                    backgroundColor: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    fontSize: '13px',
                    fontWeight: 600,
                    fontFamily: FONT,
                    color: isActive ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.45)',
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pedidos-scroll px-4 sm:px-6 py-5">
          {activeTab === 'datos' && (
            <div className="flex flex-col md:flex-row gap-6">
              {/* Left — Image */}
              <div className="flex flex-col gap-3 w-full md:w-[240px] md:shrink-0 md:border-r md:border-white/[0.16] md:pr-6">
                <div className="min-w-0">
                  <h3
                    className="font-semibold m-0 truncate"
                    style={{
                      fontSize: '15px',
                      lineHeight: '20px',
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontFamily: FONT,
                    }}
                  >
                    {form.nombre || 'Sin nombre'}
                  </h3>
                  <p
                    className="m-0 mt-0.5"
                    style={{
                      fontSize: '12px',
                      lineHeight: '16px',
                      fontWeight: 600,
                      color: 'rgba(255, 255, 255, 0.35)',
                      fontFamily: FONT,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    #{product.id}
                  </p>
                </div>

                {form.imagenes.length > 0 ? (
                  <img
                    src={form.imagenes[selectedImageIndex]}
                    alt={form.nombre}
                    style={{
                      width: '100%',
                      height: '220px',
                      objectFit: 'contain',
                      borderRadius: '20px',
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      padding: '8px',
                      boxSizing: 'border-box',
                    }}
                  />
                ) : (
                  <div
                    className="flex flex-col items-center justify-center gap-2"
                    style={{
                      width: '100%',
                      height: '220px',
                      borderRadius: '20px',
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    }}
                  >
                    <span
                      className="flex items-center justify-center w-11 h-11 rounded-2xl"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.035)' }}
                    >
                      <IconPhotoFilled size={22} color="rgba(255, 255, 255, 0.3)" />
                    </span>
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: 'rgba(255, 255, 255, 0.4)',
                        fontFamily: FONT,
                      }}
                    >
                      Sin imágenes
                    </span>
                  </div>
                )}

                {form.imagenes.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pedidos-scroll" style={{ paddingBottom: '2px', width: '100%' }}>
                    {form.imagenes.map((img, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setSelectedImageIndex(i)}
                        className="shrink-0 touch-manipulation"
                        style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '14px',
                          overflow: 'hidden',
                          border: selectedImageIndex === i ? '2px solid #0a84ff' : '2px solid transparent',
                          cursor: 'pointer',
                          padding: 0,
                          backgroundColor: 'rgba(255, 255, 255, 0.03)',
                          opacity: selectedImageIndex === i ? 1 : 0.6,
                          transition: 'opacity 0.2s ease, border-color 0.2s ease',
                        }}
                      >
                        <img src={img} alt={`Imagen ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </button>
                    ))}
                  </div>
                )}

                <div className="flex flex-col gap-2" style={{ width: '100%' }}>
                  <input
                    type="url"
                    value={driveUrl}
                    onChange={(e) => setDriveUrl(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddDriveImage()}
                    placeholder="URL de la imagen en Google Drive"
                    aria-label="URL de la imagen en Google Drive"
                    className={inputClass}
                    style={{ ...inputStyle, height: '44px', fontSize: '14px' }}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleAddDriveImage}
                      disabled={!driveUrl.trim()}
                      className="flex items-center justify-center gap-2 flex-1 h-11 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out [@media(hover:hover)]:hover:bg-white/[0.85]"
                      style={{
                        backgroundColor: '#fff',
                        fontSize: '14px',
                        fontWeight: 600,
                        fontFamily: FONT,
                        color: '#000',
                        cursor: driveUrl.trim() ? 'pointer' : 'not-allowed',
                        opacity: driveUrl.trim() ? 1 : 0.5,
                      }}
                    >
                      <IconLink size={16} color="rgba(0, 0, 0, 0.6)" />
                      Agregar
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const newImages = form.imagenes.filter((_, i) => i !== selectedImageIndex);
                        setForm((prev) => ({ ...prev, imagenes: newImages }));
                        setSelectedImageIndex(0);
                      }}
                      disabled={form.imagenes.length === 0}
                      aria-label="Eliminar imagen"
                      className="flex items-center justify-center shrink-0 w-11 h-11 rounded-full border-none transition-all duration-200 ease-out active:scale-[0.95] [@media(hover:hover)]:hover:brightness-110"
                      style={{
                        backgroundColor: form.imagenes.length > 0 ? '#ff453a' : 'rgba(255, 255, 255, 0.05)',
                        cursor: form.imagenes.length > 0 ? 'pointer' : 'not-allowed',
                        boxShadow: form.imagenes.length > 0
                          ? '0 6px 18px rgba(255, 69, 58, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.18)'
                          : 'none',
                      }}
                    >
                      <IconTrashXFilled
                        size={17}
                        color={form.imagenes.length > 0 ? '#fff' : 'rgba(255, 255, 255, 0.25)'}
                      />
                    </button>
                  </div>
                </div>

                <TipBlue>
                  Pega la URL de la imagen en Google Drive. El archivo debe estar compartido como "Cualquier persona con el enlace" para que se vea en el punto de venta.
                </TipBlue>
              </div>

              {/* Right — Data fields */}
              <div className="flex flex-col gap-5 flex-1" style={{ minWidth: 0 }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-5 items-start">
                <div className="flex flex-col gap-2">
                  <label style={labelStyle}>Nombre *</label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    placeholder="Ej: Café Latte"
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label style={labelStyle}>Precio *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.precio}
                    onChange={(e) => handleChange('precio', e.target.value)}
                    placeholder="$0.00"
                    className={inputClass}
                    style={inputStyle}
                  />
                  {form.precio && !isNaN(parseFloat(form.precio)) && parseFloat(form.precio) > 0 && (
                    <div className="flex flex-col gap-1.5 mt-1">
                      <div className="flex items-center justify-between gap-3">
                        <span style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)', fontFamily: FONT }}>
                          Cobrado
                        </span>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.7)', fontFamily: FONT, fontVariantNumeric: 'tabular-nums' }}>
                          ${parseFloat(form.precio).toFixed(2)}
                        </span>
                      </div>
                      {taxRate > 0 && (
                        <div className="flex items-center justify-between gap-3">
                          <span style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)', fontFamily: FONT }}>
                            IVA ({taxRate}%)
                          </span>
                          <span style={{ fontSize: '13px', fontWeight: 500, color: AMBER, fontFamily: FONT, fontVariantNumeric: 'tabular-nums' }}>
                            -${(parseFloat(form.precio) * taxRate / 100).toFixed(2)}
                          </span>
                        </div>
                      )}
                      {commissionRate > 0 && (
                        <div className="flex items-center justify-between gap-3">
                          <span style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)', fontFamily: FONT }}>
                            Comisión crédito ({commissionRate}%)
                          </span>
                          <span style={{ fontSize: '13px', fontWeight: 500, color: AMBER, fontFamily: FONT, fontVariantNumeric: 'tabular-nums' }}>
                            -${(parseFloat(form.precio) * commissionRate / 100).toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-3 pt-1">
                        <span style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.55)', fontFamily: FONT }}>
                          Recibes
                        </span>
                        <span style={{ fontSize: '14px', fontWeight: 600, color: GREEN, fontFamily: FONT, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>
                          ${(parseFloat(form.precio) * (1 - taxRate / 100) * (1 - commissionRate / 100)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label style={labelStyle}>Descripción</label>
                  <textarea
                    value={form.descripcion}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    placeholder="Describe el producto..."
                    rows={3}
                    className={`${inputClass} resize-y`}
                    style={{
                      ...inputStyle,
                      borderRadius: '18px',
                      height: 'auto',
                      padding: '14px 16px',
                      minHeight: '96px',
                    }}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-5 items-start">
                <SelectorField
                  label="Categorías *"
                  placeholder="Seleccionar categorías"
                  hasValue={form.category_ids.length > 0}
                  value={selectedCategoryNames.join(', ')}
                  open={showCategoriasDropdown}
                  onToggle={() => setShowCategoriasDropdown(!showCategoriasDropdown)}
                >
                  {categories.map((cat) => {
                    const isSelected = form.category_ids.includes(cat.id);
                    return (
                      <Chip
                        key={cat.id}
                        selected={isSelected}
                        onClick={() => {
                          const newIds = isSelected
                            ? form.category_ids.filter((id) => id !== cat.id)
                            : [...form.category_ids, cat.id];
                          const primary = newIds.length > 0 ? newIds[0] : null;
                          setForm((prev) => ({ ...prev, category_id: primary, category_ids: newIds }));
                        }}
                      >
                        {cat.name}
                      </Chip>
                    );
                  })}
                </SelectorField>

                <SelectorField
                  label="Variantes"
                  placeholder="Seleccionar variantes"
                  hasValue={form.variantes.length > 0}
                  value={`${form.variantes.length} seleccionada${form.variantes.length > 1 ? 's' : ''}`}
                  open={showVariantesDropdown}
                  onToggle={() => setShowVariantesDropdown(!showVariantesDropdown)}
                >
                  {variantGroups.map((g) => (
                    <Chip
                      key={g.id}
                      selected={form.variantes.includes(g.id)}
                      onClick={() => toggleVariante(g.id)}
                    >
                      {g.name}
                    </Chip>
                  ))}
                </SelectorField>
                </div>

                <div className="flex flex-col gap-2">
                  <label style={labelStyle}>Destacado / Vista</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleChange('destacado', !form.destacado)}
                      aria-pressed={form.destacado}
                      className="flex items-center justify-center gap-2 h-12 flex-1 rounded-2xl border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out [@media(hover:hover)]:hover:bg-white/[0.06]"
                      style={{
                        backgroundColor: form.destacado ? 'rgba(248, 124, 43, 0.12)' : 'rgba(255, 255, 255, 0.04)',
                        fontSize: '14px',
                        fontWeight: 600,
                        fontFamily: FONT,
                        color: form.destacado ? AMBER : 'rgba(255, 255, 255, 0.5)',
                      }}
                    >
                      <IconStarFilled size={16} color={form.destacado ? AMBER : 'rgba(255, 255, 255, 0.3)'} />
                      {form.destacado ? 'Destacado' : 'No destacado'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChange('vista', 'barra')}
                      aria-pressed={form.vista === 'barra'}
                      className="flex items-center justify-center gap-2 h-12 flex-1 rounded-2xl border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out [@media(hover:hover)]:hover:bg-white/[0.06]"
                      style={{
                        backgroundColor: form.vista === 'barra' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.04)',
                        fontSize: '14px',
                        fontWeight: 600,
                        fontFamily: FONT,
                        color: form.vista === 'barra' ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                      }}
                    >
                      <IconGlassFilled size={16} />
                      Barra
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChange('vista', 'cocina')}
                      aria-pressed={form.vista === 'cocina'}
                      className="flex items-center justify-center gap-2 h-12 flex-1 rounded-2xl border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out [@media(hover:hover)]:hover:bg-white/[0.06]"
                      style={{
                        backgroundColor: form.vista === 'cocina' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.04)',
                        fontSize: '14px',
                        fontWeight: 600,
                        fontFamily: FONT,
                        color: form.vista === 'cocina' ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                      }}
                    >
                      <IconChefHatFilled size={16} />
                      Cocina
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ingredientes' && (
            <div className="flex flex-col gap-5">
              <TipBlue>
                Tip: Aquí defines los ingredientes (insumos de inventario) que componen este producto. Al venderse, el stock de cada insumo se descuenta automáticamente.
              </TipBlue>

              <div className="flex flex-col md:flex-row gap-6">
                {/* Left — Available inventory items */}
                <div className="flex flex-col gap-3 flex-1" style={{ minWidth: 0 }}>
                  <div className="relative w-full">
                    <IconSearchFilled
                      size={16}
                      color="rgba(255, 255, 255, 0.35)"
                      style={{
                        position: 'absolute',
                        left: '16px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                      }}
                    />
                    <input
                      type="text"
                      value={inventorySearch}
                      onChange={(e) => setInventorySearch(e.target.value)}
                      placeholder="Buscar insumo..."
                      className={inputClass}
                      style={{ ...inputStyle, padding: '0 16px 0 44px' }}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {filteredInventory.map((inv) => (
                      <button
                        key={inv.id}
                        type="button"
                        onClick={() =>
                          setForm((prev) => {
                            if (prev.ingredientes.some((x) => x.inventory_id === inv.id)) {
                              dynamicIslandRef?.current?.addAlert('error', 'El ingrediente ya está agregado');
                              return prev;
                            }
                            return {
                              ...prev,
                              ingredientes: [
                                ...prev.ingredientes,
                                {
                                  inventory_id: inv.id,
                                  inventory_name: inv.name,
                                  quantity: '1',
                                  unit_of_measure: inv.unit_of_measure,
                                },
                              ],
                            };
                          })
                        }
                        className="flex items-center justify-between gap-3 w-full min-h-[48px] px-4 rounded-2xl border-none cursor-pointer touch-manipulation select-none text-left transition-colors duration-200 ease-out [@media(hover:hover)]:hover:bg-white/[0.06]"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.03)',
                          fontFamily: FONT,
                        }}
                      >
                        <span
                          className="truncate"
                          style={{ fontSize: '14px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.9)' }}
                        >
                          {inv.name}
                        </span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span style={{ fontSize: '12px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.35)' }}>
                            {inv.unit_of_measure}
                          </span>
                          <IconPlusFilled size={14} color="rgba(255, 255, 255, 0.3)" />
                        </span>
                      </button>
                    ))}
                    {filteredInventory.length === 0 && (
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: 500,
                          color: 'rgba(255, 255, 255, 0.35)',
                          fontFamily: FONT,
                          textAlign: 'center',
                          padding: '20px 0',
                        }}
                      >
                        Sin resultados
                      </span>
                    )}
                  </div>
                </div>

                {/* Right — Selected ingredients */}
                <div className="flex flex-col gap-3 flex-1 md:border-l md:border-white/[0.16] md:pl-6" style={{ minWidth: 0 }}>
                  <div className="flex items-center justify-between gap-3">
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: 'rgba(255, 255, 255, 0.45)',
                        fontFamily: FONT,
                      }}
                    >
                      Ingredientes
                    </span>
                    <span
                      className="inline-flex items-center justify-center shrink-0 h-7 min-w-[28px] rounded-full px-2.5"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontFamily: FONT,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {form.ingredientes.length}
                    </span>
                  </div>
                  {form.ingredientes.length > 0 ? (
                    <div className="flex flex-col gap-1.5">
                      {form.ingredientes.map((ing, i) => (
                        <div
                          key={`${ing.inventory_id}-${i}`}
                          className="flex items-center gap-2 min-h-[52px] px-3 rounded-2xl"
                          style={{ backgroundColor: 'rgba(255, 255, 255, 0.025)' }}
                        >
                          <span
                            className="flex-1 truncate"
                            style={{
                              fontSize: '14px',
                              fontWeight: 500,
                              color: 'rgba(255, 255, 255, 0.9)',
                              fontFamily: FONT,
                            }}
                          >
                            {ing.inventory_name}
                          </span>
                          <span
                            className="shrink-0"
                            style={{
                              fontSize: '12px',
                              fontWeight: 500,
                              color: 'rgba(255, 255, 255, 0.35)',
                              fontFamily: FONT,
                            }}
                          >
                            {ing.unit_of_measure}
                          </span>
                          <input
                            type="number"
                            min="0.01"
                            step="0.1"
                            value={ing.quantity}
                            aria-label={`Cantidad de ${ing.inventory_name}`}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                ingredientes: prev.ingredientes.map((x, idx) =>
                                  idx === i ? { ...x, quantity: e.target.value } : x
                                ),
                              }))
                            }
                            className="shrink-0 outline-none bg-white/[0.08] focus:bg-white/[0.12] transition-colors duration-200"
                            style={{
                              width: '76px',
                              height: '40px',
                              border: 'none',
                              borderRadius: '9999px',
                              padding: '0 12px',
                              fontSize: '14px',
                              fontWeight: 600,
                              fontFamily: FONT,
                              color: 'rgba(255, 255, 255, 0.95)',
                              textAlign: 'center',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          />
                          <DeleteIconCircle
                            solid
                            size={40}
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                ingredientes: prev.ingredientes.filter((_, idx) => idx !== i),
                              }))
                            }
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState
                      icon={IconBoxMultipleFilled}
                      title="Sin ingredientes aún"
                      description="Agrega insumos del inventario para armar la receta."
                      className="py-4"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-end gap-2 shrink-0 px-4 sm:px-6 py-4 border-t border-white/[0.16]">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex items-center justify-center h-12 px-6 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-white/[0.1]"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: FONT,
              color: 'rgba(255, 255, 255, 0.9)',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.5 : 1,
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="flex items-center justify-center h-12 px-7 rounded-full border-none font-semibold transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-[#0077ed]"
            style={{
              backgroundColor: canSave ? '#0071e3' : 'rgba(255, 255, 255, 0.06)',
              fontSize: '15px',
              letterSpacing: '-0.01em',
              fontFamily: FONT,
              color: canSave ? '#fff' : 'rgba(255, 255, 255, 0.35)',
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
          >
            {saving ? 'Actualizando…' : 'Actualizar'}
          </button>
        </div>
      </div>
    </div>
  );
}
